'use strict';

import React, { StrictMode, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

const $ = jQuery;

const Http = {
    isRequestPending: false,
    lastError: null,
    xhr: null,
    request(options) {
        if (this.isRequestPending) {
            this.xhr.abort();
        }
        this.isRequestPending = true;
        this.lastError = null;
        return new Promise((resolve, reject) => {
            this.xhr = $.ajax($.extend(true, {}, options, {
                success: (data, textStatus, jqXhr) => {
                    var response = {data: data, textStatus: textStatus, jqXhr: jqXhr};
                    console.debug('Http.request success', response);
                    this.isRequestPending = false;
                    resolve(response);
                },
                error: (jqXhr, textStatus, errorThrown) => {
                    var err = {code: jqXhr.status, message: jqXhr.statusText, jqXhr: jqXhr, textStatus: textStatus, errorThrown: errorThrown};
                    this.lastError = err;
                    console.debug('Http.request error', err);
                    this.isRequestPending = false;
                    reject(err);
                }
            }));
        });
    }
}

const Timer = class {
	options = {
		delay: 0,
		interval: 1000,
		callback: null
	};
	id = 0;
    lastStartTime = null;
    lastStopTime = null;
	constructor(options) {
		this.options = jQuery.extend({}, this.options, options);
	}
	start(callback) {
        this.stop();
        this.lastStartTime = Date.now();
        this.id = setTimeout(() => {
            (callback ?? this.options.callback)();
            this.id = 0;
        }, this.options.delay || this.options.interval);
		return this;
	}
	stop() {
        this.lastStopTime = Date.now();
        if (this.id !== 0) {
            clearTimeout(this.id);
            this.id = 0;
        }
		return this;
	}
};

function i18nTranslate(text, lang = null) {
    if (lang == null) lang = window.PwRentalLanguage;
    return window.PwRentalTranslations[lang][text];
}

function RangeInput({name, value, label, handleInput, range, format}) {

    const [values, setValues] = useState(value?.map?.(val => Number(String(val).unformat(format)[0])));
    const [currentValue, setCurrentValue] = useState(null);
    const controlRef = useRef(null);
    const sliderRef = useRef(null);

    useEffect(() => {
        if (sliderRef.current === null) {
            sliderRef.current = $(controlRef.current).slider({
                range: true,
                min: range[0] || 0,
                max: range[1] || 0,
                values: [...values],
                slide: (event, ui) => {
                    setValues(ui.values);
                },
                change: (event, ui) => {
                    setValues(ui.values);
                },
                stop: (event, ui) => {
                    setCurrentValue(ui.values);
                }
            });
        }
        return () => {
            if (sliderRef.current) {
                $(sliderRef.current).slider('destroy');
            }
        };
    }, []);

    useEffect(() => {
        setValues(value);
        if (sliderRef.current) {
            $(sliderRef.current).slider('values', value);
        }
    }, [value]);

    useEffect(() => {
        if (currentValue !== null) {
            handleInput(currentValue);
        }
    }, [currentValue]);

    return (
        <div className='pw-field field-range'>
            <label className='label'>{label}</label>
            <div className='input-wrapper'>
                <div className='d-flex'>
                    <div className='control ui-slider ui-corner-all ui-slider-horizontal ui-widget ui-widget-content' ref={controlRef}></div>
                    <span className='text'>{format.format(values[0]) + ' - ' + format.format(values[1])}</span>
                    <input type='hidden' name={name} value={values} />
                </div>
            </div>
        </div>
    );
}

function ButtonListInput({name, value, label, handleInput, options}) {
    return (
        <div className='pw-field field-buttonlist layout-horizontal'>
            <label className='label'>{label}</label>
            <div className='input-wrapper'>
                <div>
                    {Object.keys(options).map((key) => (
                        <button
                            key={key}
                            type='button'
                            name={name}
                            value={key}
                            className={'pw-btn pw-btn-small' + (value?.includes?.(key) ? ' pw-btn-primary' : ' pw-btn-secondary')}
                            onClick={(event) => {
                                handleInput(event.target.value);
                            }}
                        >{options[key]}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function TextInput({name, value, label, handleInput}) {
    return (
        <div className='pw-field field-text'>
            <label className='label'>{label}</label>
            <input type='text' name={name} value={value ?? ''} onInput={(event) => {
                handleInput(event.target.value);
            }} />
        </div>
    );
}

function FilterInput({input, value, handleInput}) {
    return (
        <div className='filter'>
            {React.createElement(input.component, {
                ...input,
                value: (value ?? input.defaultValue),
                handleInput: (val) => {
                    handleInput(val);
                }
            })}
        </div>
    );
}

function openRequestQuotation({title}) {
    let elem = $('.modal.request-quotation');
    elem.find('.product-name input').val(title).prop('readonly', true);
    let viewportWidth = $(window).width();
    elem.dialog({
        autoOpen: true,
        modal: true,
        draggable: false,
        resizable: false,
        width: Math.min(600, viewportWidth),
        position: { my: "center center", at: "center center", of: window, within: '#main' },
        open: function(event, ui) {
            elem.find('.name input').focus();
            $('.ui-widget-overlay').on('click', () => {
                elem.dialog('close');
            });
            // $('.ui-dialog-titlebar-close').hide();
        },
        // buttons: [
        //     {
        //         text: (elem.data('close-label') ?? 'Close'),
        //         icon: '',
        //         click: () => {
        //             elem.dialog('close');
        //         }
        //     }
        // ]
    });
}

function ListItem({item, filters, filterInputs}) {
    const [params, setParams] = useState({});
    useEffect(() => {
        setParams((currentValue) => {
            return {...filters, ...{max_working_height: 1, max_platform_height: 1, load_capacity: 1}};
        });
    }, [filters]);
    const linkRef = useRef();
    useEffect(() => {
        let el = $(linkRef.current),
            rel = el.data('rel');
        el.iLightBox(window.avadaLightBox.prepare_options(rel));
    }, [linkRef]);
    return (
        <div className='pw-rental-product-card'>
            <div className='row'>
                <div className='col-xs-12 col-md-4 product-thumbnail'>
                    {/* <a href={item.url}><img src={item.thumbnail} /></a> */}
                    <a href={item.image} data-rel={'iLightbox[product_' + item.id + ']'} ref={linkRef}><img src={item.thumbnail} /></a>
                </div>
                <div className='col-xs-12 col-md-8'>
                    <h3><a href={item.url}>{item.title}</a></h3>
                    <div className='short-description'>{item.description}</div>
                    {Object.keys(params).length > 0 ? (
                        <table className='table product-params'>
                            <tbody>
                                {Object.keys(params).map(key => {
                                    let paramName = filterInputs[key].param;
                                    let param = item.parameters[paramName];
                                    return param ? (
                                        <tr key={key} className={filters[key] ? 'active' : ''}>
                                            <td>{param.label}</td>
                                            <td>{param.formatted_value}</td>
                                        </tr>
                                    ) : '';
                                })}
                            </tbody>
                        </table>
                    ) : ''}
                    <div className='buttons'>
                        <button type='button' className='fusion-button button-flat button-large button-default fusion-button-default btn request-quotation' onClick={() => openRequestQuotation(item)}>{i18nTranslate('Request Quotation')}</button>
                        <a href={item.url} className='fusion-button button-flat button-large button-default fusion-button-default pw-btn pw-btn-red'>{i18nTranslate('Details')}</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

let App = function PontwebRental() {
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [filterInputs, setFilterInputs] = useState({});
    const getFilterFormattedValue = (key) => {
        let input = filterInputs[key], value, separator = ', ';
        if (input.component === RangeInput) {
            separator = ' - ';
        }
        if (input.options) {
            value = filters[key].map(value => input.options[value]);
        } else {
            value = filters[key];
        }
        if (value.constructor === Array) {
            return value.map(value => input.format.format(value)).join(separator);
        } else {
            return input.format.format(value);
        }
    };
    
    const filtersRef = useRef(filters);
    let loadTimerRef = useRef(null);

    const loadItemsWithDelay = () => {
        if (loadTimerRef.current === null) {
            loadTimerRef.current = new Timer({delay: 300, callback: () => {
                loadItems();
            }});
        }
        let loadTimer = loadTimerRef.current;
        // console.log('loadTimer', loadTimer, loadTimer.lastStopTime);
        let elapsedTime = Date.now() - (loadTimer.lastStopTime ?? 0);
        loadTimer.stop();
        if (elapsedTime < loadTimer.options.delay) {
            loadTimer.start();
        } else {
            loadItems();
        }
    };
    const loadItems = () => {
        console.debug('filters', filtersRef.current);
        setLoading(true);
        Http.request({
            type: 'GET',
            url: '/wp-admin/admin-ajax.php?action=pw_rental_products&output=json',
            data: {...filtersRef.current},
            dataType: 'json'
        }).then((response) => {
            setTotalCount(response.data?.totalCount);
            setItems(response.data?.items);
        }).finally(() => {
            setLoading(false);
        });
    }
    useEffect(() => {
        filtersRef.current = filters;
        loadItemsWithDelay();
    }, [filters]);

    useEffect(() => {
        let inputs = {};
        let typeComponentMap = {
            range: RangeInput,
            buttonlist: ButtonListInput,
        };
        Object.keys(PwRentalSearchFields).map((key) => {
            let field = PwRentalSearchFields[key];
            let input = {
                component: typeComponentMap[field.type],
                label: field.label,
                name: key,
                param: key,
                format: (field.format ?? '{0}'),
            };
            if (field.valid_values != null) {
                input.options = {...field.valid_values};
                input.multiple = field.type === 'buttonlist';
            }
            if (field.min != null) {
                input.range = [field.min, field.max];
                input.defaultValue = [field.min, field.max];
            }
            inputs[key] = input;
        });
        setFilterInputs(inputs);
    }, []);

    const setFilter = (name, value) => {
        setFilters((currentValue) => {
            if (value == null || value.length === 0) {
                delete currentValue[name];
            } else {
                currentValue = {
                    ...currentValue,
                    [name]: value
                };
            }
            return {...currentValue};
        });
    }
    const clearFilters = () => {
        setFilters({});
    };

    return (
        <div className='row'>
            <div className='col-xs-12 col-md-3 filter-form'>
                <div className='filters'>
                    {Object.keys(filterInputs).map((key) => (
                        <FilterInput
                            key={key}
                            input={filterInputs[key]}
                            value={filters[filterInputs[key].name]}
                            handleInput={(value) => {
                                let input = filterInputs[key];
                                if (input.multiple) {
                                    let currentValue = filters[input.name] ?? [],
                                        currentValuePos = currentValue.indexOf(value);
                                    if (currentValuePos !== -1) {
                                        currentValue.splice(currentValuePos, 1);
                                        value = [...currentValue];
                                    } else {
                                        value = [...currentValue, value];
                                    }
                                }
                                setFilter(input.name, value);
                            }}
                        />
                    ))}
                </div>
            </div>
            <div className='col-xs-12 col-md-9 product-list'>
                <div className='active-filters' style={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: '#e5e5e5',
                    backgroundImage: 'linear-gradient(180deg, #f5f5f5 0%,#e0e0e0 100%)',
                    padding: '5px',
                    minHeight: '60px',
                    display: (Object.keys(filters).length > 0 || isLoading ? 'flex' : 'none')
                }}>
                    <div style={{width: `${8/12*100}%`}}>
                        {Object.keys(filters).length > 0 ? (
                            <div className='search-active-filters d-flex' style={{flexWrap: 'wrap'}}>
                                {Object.keys(filters).map(key => (
                                    <div key={key} className='filter'>
                                        <span className='label' style={{marginRight: '5px'}}>{filterInputs[key].label}:</span>
                                        <span className='value'>{getFilterFormattedValue(key)}</span>
                                        <button type='button' onClick={() => setFilter(key, null)}>x</button>
                                    </div>
                                ))}
                            </div>
                        ) : ''}
                    </div>
                    <div style={{width: `${2/12*100}%`, textAlign: 'right'}}>
                        {Object.keys(filters).length > 0 ? (
                            <button type='button' className='fusion-button button-flat button-small button-custom fusion-button-default button-4 fusion-button-default-span fusion-button-default-type clear-all' onClick={() => clearFilters()} title={i18nTranslate('Clear filters')} style={{
                                '--button_accent_color': 'var(--awb-color1)',
                                '--button_accent_hover_color': 'var(--awb-color1)',
                                '--button_border_hover_color': 'var(--awb-color1)',
                                '--button_gradient_top_color': 'var(--awb-custom_color_10)',
                                '--button_gradient_bottom_color': 'var(--awb-custom_color_10)',
                                '--button_gradient_top_color_hover': 'var(--awb-custom_color_11)',
                                '--button_gradient_bottom_color_hover': 'var(--awb-custom_color_11)',
                                'display': ' inline',
                            }}><i className='fa-funnel-dollar fas button-icon-left' style={{marginRight: '0px'}}></i></button>
                        ) : ''}
                    </div>
                    <div style={{width: `${2/12*100}%`, display: 'flex', justifyContent: 'center'}}>
                        <i className='fb-icon-element-1 fb-icon-element fontawesome-icon fa-spinner fas circle-yes fa-spin spinner' style={{
                            '--awb-iconcolor': '#666666',
                            '--awb-iconcolor-hover': '#666666',
                            '--awb-circlecolor': 'transparent',
                            '--awb-circlecolor-hover': 'transparent',
                            '--awb-circlebordercolor': 'transparent',
                            '--awb-circlebordercolor-hover': 'transparent',
                            '--awb-circlebordersize': '1px',
                            '--awb-font-size': '17.6px',
                            '--awb-width': '35.2px',
                            '--awb-height': '35.2px',
                            '--awb-line-height': '33.2px',
                            '--awb-margin-top': '0',
                            '--awb-margin-right': '0',
                            '--awb-margin-bottom': '0',
                            '--awb-margin-left': '0',
                            '--awb-align-self': 'center',
                            visibility: (isLoading ? 'visible' : 'hidden')
                        }}></i>
                    </div>
                </div>
                {items.length === 0 ? (
                    isLoading
                        ? (<div style={{marginTop: '30px', textAlign: 'center'}}>{i18nTranslate('Retrieving data...')}</div>)
                        : (<div style={{marginTop: '30px', textAlign: 'center'}}>{i18nTranslate('No results')}</div>)
                ) : ''}
                <div className='list'>
                    <div className='list-info'>
                        {totalCount > 0 ? (
                            <div className='count'>
                                <label>{i18nTranslate('Results')}:</label> <span>{totalCount}</span>
                            </div>
                        ) : ''}
                    </div>
                    <div className='row products'>
                        {items.map((item, index) => (
                            <div key={item.id} className='col-xs-12 col-sm-12'>
                                <ListItem item={item} filters={filters} filterInputs={filterInputs} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

$(document).ready(function () {
    const rootNode = $('#pw-rental.react-app');
    if (rootNode.length > 0) {
        console.debug('rootNode', rootNode);
        const root = createRoot(rootNode[0]);
        // root.render(<StrictMode><App/></StrictMode>);
        root.render(<App/>);
    }
});
